import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormMultiSelectField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { isEmpty } from '@/utils/is-empty'
import { isTickmillCompanyDisabled } from '@/utils/isTickmillCompanyDisabled'
import { CreateReferralMaterial } from 'api/ClientArea/CreateReferralMaterial'
import { ReferralMaterialsDto } from 'api/ClientArea/ReferralMaterialsDto'
import { Language } from 'api/Clients/Types/Language'
import { TickmillCompanyTypes } from 'api/Clients/Types/TickmillCompanyTypes'
import { NameDto } from 'api/Common/Types'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { Form, FormikProps, withFormik } from 'formik'
import { AuthSessionContext } from 'utils/AuthContext'
import { convertDateToUTC, createDateAsUTC } from 'utils/date'
import { FormSubmitValues } from 'utils/formValidation'

export interface ReferralMaterialsFormValues {
  name: string
  caLanguages: string[]
  typeId: number | undefined
  categoryIds: string[]
  tickmillCompanyId: number | undefined
  enabled: boolean
  endDateTime: string | null
}

type ReferralMaterialsFormProps = FormikProps<ReferralMaterialsFormValues> & OuterProps

const ReferralMaterialsFormUI: React.FC<ReferralMaterialsFormProps> = (props) => {
  const { errors, handleSubmit, values, setFieldValue, isValid, isSubmitting } = props
  const {
    tickmillCompanies,
    referralMaterialsCategoriesByEntity,
    referralMaterialsTypes,
    fetchReferralMaterialsCategoriesByEntity,
    languages,
  } = props
  const [auth] = useContext(AuthSessionContext)
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false)
  const previousTickmillCompanyIdRef = useRef<number | undefined>()

  const dateFormatType = auth?.user?.dateFormatType?.name
  const companyOptions = useMemo(
    () =>
      tickmillCompanies.map((company) => ({
        id: company.id,
        name: company.name,
        disabled: isTickmillCompanyDisabled(company),
      })),
    [tickmillCompanies]
  )

  useEffect(() => {
    if (values.tickmillCompanyId == null) {
      previousTickmillCompanyIdRef.current = undefined
      return
    }

    const hasCompanyChanged = previousTickmillCompanyIdRef.current !== values.tickmillCompanyId
    previousTickmillCompanyIdRef.current = values.tickmillCompanyId

    if (!hasCompanyChanged) {
      return
    }

    void setFieldValue('categoryIds', [])
    setIsCategoriesLoading(true)
    fetchReferralMaterialsCategoriesByEntity(values.tickmillCompanyId, true).finally(() =>
      setIsCategoriesLoading(false)
    )
  }, [fetchReferralMaterialsCategoriesByEntity, setFieldValue, values.tickmillCompanyId])

  return (
    <DialogContent size='sm' render={<Form />}>
      <DialogHeader>
        <DialogTitle>
          {props.data.id ? 'Edit Referral Material' : 'Add Referral Material'}
        </DialogTitle>
      </DialogHeader>
      <FormField error={errors.name} required name='name' label='Referral Material Name' />
      <FormMultiSelectField
        error={errors.caLanguages?.toString()}
        name='caLanguages'
        label='CA Language'
        required
        value={values.caLanguages}
        options={languages}
        showSearch
        allowClear
        onChange={(value) =>
          setFieldValue(
            'caLanguages',
            Array.isArray(value) ? value.map(String) : value ? [String(value)] : []
          )
        }
      />
      <FormSelectField
        name='typeId'
        label='Type'
        required
        error={errors.typeId}
        value={values.typeId}
        options={referralMaterialsTypes}
        showSearch
        allowClear
        onChange={(value) => setFieldValue('typeId', value ? Number(value) : undefined)}
      />
      <FormSelectField
        error={errors.tickmillCompanyId}
        name='tickmillCompanyId'
        label='Tickmill Entity'
        required
        value={values.tickmillCompanyId}
        options={companyOptions}
        showSearch
        allowClear
        onChange={(value) => setFieldValue('tickmillCompanyId', value ? Number(value) : undefined)}
      />
      <FormMultiSelectField
        error={errors.categoryIds?.toString()}
        name='categoryIds'
        label='Category'
        value={values.categoryIds}
        options={referralMaterialsCategoriesByEntity}
        showSearch
        allowClear
        disabled={isCategoriesLoading}
        onChange={(value) =>
          setFieldValue(
            'categoryIds',
            Array.isArray(value) ? value.map(String) : value ? [String(value)] : []
          )
        }
      />
      <FormDateField
        error={errors.endDateTime}
        name='endDateTime'
        label='End Date'
        dateFormatType={dateFormatType}
      />
      <FormCheckboxField
        error={errors.enabled}
        name='enabled'
        label='Active'
        value={values.enabled}
      />

      <DialogFooter>
        <Button type='button' onClick={() => handleSubmit()} disabled={!isValid || isSubmitting}>
          Save
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}

const dataToSubmit = (values: Required<ReferralMaterialsFormValues>): CreateReferralMaterial => {
  return {
    name: values.name || '',
    caLanguages: values.caLanguages || [],
    typeId: values.typeId || 0,
    categoryIds: values.categoryIds || [],
    endDateTime: values.endDateTime
      ? createDateAsUTC(values.endDateTime, false, true).toISOString()
      : null,
    enabled: values.enabled || false,
    tickmillCompanyId: values?.tickmillCompanyId || 0,
  }
}

interface OuterProps {
  data: ReferralMaterialsDto
  languages: Language[]
  tickmillCompanies: TickmillCompanyDto[]
  referralMaterialsTypes: NameDto[]
  referralMaterialsCategoriesByEntity: (NameDto<string> & { isActive: boolean })[]
  fetchReferralMaterialsCategoriesByEntity: (
    tickmillCompanyId: number | string,
    isCategoryActive: boolean
  ) => Promise<(NameDto<string> & { isActive: boolean })[]>
  onSubmit(values: FormSubmitValues<CreateReferralMaterial>): Promise<void>
}

const getDefaultCompanyId = (companies: TickmillCompanyDto[]): TickmillCompanyTypes | undefined => {
  return companies.find((x) => x.id === TickmillCompanyTypes.Tickmill_SC_Company)?.id
}

const getDefaultLanguage = (languages: Language[]): NameDto<string> | undefined => {
  return languages.find((x) => x.id === 'en')
}

export const ReferralMaterialsForm = withFormik<OuterProps, ReferralMaterialsFormValues>({
  mapPropsToValues: (props) => {
    const { data } = props
    const defaultLanguage = getDefaultLanguage(props.languages)
    const defaultCompany = getDefaultCompanyId(props.tickmillCompanies)
    const categoryIds = (data.categories ?? []).map((category) => category.id)

    return {
      name: data?.name || '',
      caLanguages: (data.caLanguages || (defaultLanguage ? [defaultLanguage] : []))?.map(
        (x) => x.id
      ),
      typeId: data?.type?.id,
      categoryIds,
      enabled: data?.enabled ?? false,
      endDateTime: data.endDateTime ? convertDateToUTC(data.endDateTime).toISOString() : null,
      tickmillCompanyId: data?.tickmillCompany?.id ?? defaultCompany,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      const data = dataToSubmit(values)
      await props.onSubmit(data)
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
  validateOnChange: true,
  validateOnMount: true,
})(ReferralMaterialsFormUI)
